import { useState } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useAuth,
} from '@aaas/shared';
import type { SlackIntegration } from '@aaas/shared';
import { AlertTriangle, Loader2, Trash2, XCircle } from 'lucide-react';
import { useDisconnectSlackIntegrationWorkspace } from '../hooks/useIntegrationQuery';

interface SlackDisconnectCardProps {
  integration: SlackIntegration;
  onDisconnectSuccess?: () => void;
}

const SlackDisconnectCard: React.FC<SlackDisconnectCardProps> = ({
  integration,
  onDisconnectSuccess,
}) => {
  const { idToken } = useAuth();
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const disconnectMutation = useDisconnectSlackIntegrationWorkspace();

  const handleDisconnect = async () => {
    if (!idToken || !integration) return;

    try {
      await disconnectMutation.mutateAsync({
        idToken,
        integrationId: integration.id,
      });
      setShowDisconnectDialog(false);

      // Call the success callback if provided
      if (onDisconnectSuccess) {
        onDisconnectSuccess();
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to disconnect';
      alert('Failed to disconnect integration: ' + errorMessage);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-red-200 p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 bg-red-100 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-900 mb-1">Danger Zone</h3>
            <p className="text-sm text-gray-600">Disconnect this Slack workspace integration</p>
          </div>
        </div>

        {/* Warning Description */}
        <div className="bg-red-50 rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            This action will:
          </p>
          <ul className="text-sm text-red-800 space-y-1 pl-5 list-disc">
            <li className="ml-2">Remove this integration from your workspace</li>
            <li className="ml-2">
              Remove all Slack user records and their access permissions for this workspace
            </li>
            <li className="ml-2">Stop the bot from responding to messages for this workspace</li>
            <li className="ml-2">
              Uninstall the Slack app from workspace "{integration.team_name}" (only if no other
              workspaces are using it)
            </li>
          </ul>
        </div>

        {/* Additional Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-yellow-800">
            <span className="font-semibold">⚠️ Warning:</span> This action cannot be undone.
            Integration data and user access records will be permanently deleted.
          </p>
        </div>

        {/* Disconnect Button */}
        <Button
          variant="destructive"
          onClick={() => setShowDisconnectDialog(true)}
          disabled={disconnectMutation.isPending}
          className="w-1/5"
        >
          {disconnectMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Disconnecting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Disconnect Slack Workspace
            </>
          )}
        </Button>
      </div>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Disconnect Slack Integration
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p>
                  Are you absolutely sure you want to disconnect the Slack integration for workspace{' '}
                  <strong>"{integration.team_name}"</strong>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm font-semibold text-red-900 mb-1">This will permanently:</p>
                  <ul className="text-sm text-red-800 space-y-1 list-disc">
                    <li className="ml-2">Remove this integration from your workspace</li>
                    <li className="ml-2">Delete all user access records for this workspace</li>
                    <li className="ml-2">
                      Uninstall the app from Slack (if no other workspaces are using it)
                    </li>
                  </ul>
                </div>
                <p className="text-sm font-semibold text-gray-900">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnectMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {disconnectMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Yes, Disconnect Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SlackDisconnectCard;
